<script lang="ts">
import { PropType } from "vue"
import { Alarm, AlarmLevel } from "@/types/alarm"
import AlarmLevelIcon from "@/components/alarms/AlarmLevelIcon.vue"
import AlarmLevelBadge from "@/components/alarms/AlarmLevelBadge.vue"
import {
    alarmSource,
    alarmTypeDescription,
    alarmTypeFix,
    alarmTypeTitle,
    formatDate,
    levelVariant,
} from "@/utils/alarm-utils"

export default {
    name: "AlarmDetail",
    components: { AlarmLevelIcon, AlarmLevelBadge },
    props: {
        alarm: {
            type: Object as PropType<Alarm>,
            required: true,
        },
    },
    methods: {
        levelVariantText(level: AlarmLevel): string {
            return levelVariant(level)
        },
        alarmSourceTitle(source: string): string {
            return alarmSource(this.$i18n, source)
        },
        formatDate,
        alarmTypeTitle(type: string): string {
            return alarmTypeTitle(this.$i18n, type)
        },
        alarmTypeDescription(type: string): string {
            return alarmTypeDescription(this.$i18n, type)
        },
        alarmTypeFix(type: string): string | null {
            return alarmTypeFix(this.$i18n, type)
        },
    },
}
</script>

<template>
    <div class="content alarm-detail">
        <button type="button" class="alarm-detail-back" @click="$emit('close')">
            <i class="fa-solid fa-chevron-left"></i>
            <span>{{ $t("notifications.back") }}</span>
        </button>

        <div :class="`alarm-detail-title-row ${levelVariantText(alarm.level)}`">
            <h4 class="alarm-detail-title">
                {{ alarmTypeTitle(alarm.type) }}
            </h4>
            <AlarmLevelIcon :level="alarm.level" large />
        </div>

        <h6 class="alarm-detail-section-title">{{ $t("notifications.alarm_time") }}</h6>
        <div class="alarm-detail-field d-flex justify-content-between">
            <span>{{ formatDate(alarm.conditionStartedAt, false) }}</span>
        </div>

        <h6 class="alarm-detail-section-title">{{ $t("notifications.alarm_description") }}</h6>
        <p class="alarm-detail-text">{{ alarmTypeDescription(alarm.type) }}</p>

        <template v-if="alarmTypeFix(alarm.type)">
            <h6 class="alarm-detail-section-title">{{ $t("notifications.how_to_fix") }}</h6>
            <div class="alarm-fix-box d-flex align-items-center">
                <i class="fa-solid fa-wrench mr-3"></i>
                <span>{{ alarmTypeFix(alarm.type) }}</span>
            </div>
        </template>

        <h6
            v-if="alarm.deviceName || alarm.locationName || alarm.source"
            class="alarm-detail-section-title"
        >
            {{ $t("notifications.details") }}
        </h6>
        <div v-if="alarm.deviceName" class="alarm-detail-field d-flex justify-content-between">
            <span>{{ $t("notifications.device_name") }}</span>
            <span class="alarm-detail-field-value">{{ alarm.deviceName ?? "-" }}</span>
        </div>
        <div v-if="alarm.locationName" class="alarm-detail-field d-flex justify-content-between">
            <span>{{ $t("notifications.location") }}</span>
            <span class="alarm-detail-field-value">{{ alarm.locationName ?? "–" }}</span>
        </div>
        <div v-if="alarm.source" class="alarm-detail-field d-flex justify-content-between">
            <span>{{ $t("notifications.source") }}</span>
            <span class="alarm-detail-field-value">{{
                alarmSourceTitle(alarm.source) ?? "-"
            }}</span>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.alarm-detail {
    padding: 0.25rem 0.25rem 0;
}

.alarm-detail-back {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: none;
    padding: 0;
    margin-bottom: 1rem;
    color: var(--theme-auxiliary);
    font-size: 0.9rem;
}

.alarm-detail-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 0.5rem;

    &.danger {
        color: var(--toast-danger-accent);
        border-left: 4px solid var(--toast-danger-accent);
    }
    &.warning {
        color: var(--toast-warning-accent);
        border-left: 4px solid var(--toast-warning-accent);
    }
    &.info {
        color: var(--toast-info-accent);
        border-left: 4px solid var(--toast-info-accent);
    }
    &.secondary {
        color: var(--theme-auxiliary);
        border-left: 4px solid var(--theme-secondary-transparent-50);
    }
}

.alarm-detail-title {
    font-size: 1.35rem;
    font-weight: 700;
    color: var(--theme-secondary);
    margin: 0;
}

.alarm-detail-level-badge {
    margin: 0.9rem 0 1.1rem;
}

.alarm-detail-section-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--theme-secondary);
    margin: 1.1rem 0 0.4rem;
}

.alarm-detail-text {
    color: var(--theme-auxiliary);
    font-size: 0.9rem;
}

.alarm-fix-box {
    border: 1px solid color-mix(in srgb, var(--toast-success-accent) 50%, transparent);
    border-radius: 0.5rem;
    color: var(--theme-secondary);
    font-size: 0.9rem;
    padding: 0.75rem 1rem;

    i {
        font-size: 1.15rem;
        color: var(--toast-success-accent);
    }
}

.alarm-detail-field {
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
    color: var(--theme-auxiliary);
}

.alarm-detail-field-value {
    color: var(--theme-secondary);
    font-weight: 600;
    text-align: right;
}
</style>
